import React, { useReducer, useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { ensureBoolean, i18nIntl, isEmpty, isNotEmpty } from '../../lib/ep';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import { v4 as uuidv4 } from 'uuid';

import { CreateButton, SquaredButton } from './ActionButton';
import ActionLink from './ActionLink';
import AddMenu from './AddMenu';
import ButtonCollection from './ButtonCollection';
import { DeleteButton } from './ConfirmButton';
import FloatingLayer from './FloatingLayer';
import FloatingSelectorList from './FloatingSelectorList';
import Input from './Input';
import NavigationTabs from './NavigationTabs';
import ToggleButton from './ToggleButton';
import SaveButton from './SaveButton';
import SectionSubtitle from './SectionSubtitle';
import SectionTitle from './SectionTitle';
import Tooltip from './Tooltip';

import BuilderSubSectionTitle from '../backend/BuilderSubSectionTitle';
import TextArea from './TextArea';
import { AutoPostImageUploader } from './ImageUploader';
import YouTubeField from './YouTubeField';
import UrlField from './UrlField';
import FieldLabel from './FieldLabel';
import RichEditor from './RichEditor';
import { AIMultiTranslate } from '../backend/elements/AITranslate.tsx';
import Spinner from './Spinner.js';

const DragHandle = SortableHandle(() => <div class="sorter_target" />);

const checkFieldLimits = (field_type, fields) => {
	const over = (field_type?.max && fields.filter((f) => (f.type == field_type.type)).length >= field_type.max);
	return over
}

const checkMinFieldLimit = (field_type, fields) => {
	const less = (field_type?.min && fields.filter((f) => (f.type == field_type.type)).length <= field_type.min);
	return less;
}

export const localizeFields = (fields,locale) => {
	return ((fields != undefined) ? fields : []).map((field) => {
		if (field.loc == undefined) return field;
		if (field.loc[locale] == undefined) return field;

		const new_field = {...field};
		for(let key in field.loc[locale]) {
			new_field[key] = field.loc[locale][key];
		}
		return new_field;
	})
};

const TranslateFieldWrapper = (props) => {
	let status_icon_class = ['gui_small_icon'];
	status_icon_class.push((props?.any_translated ?? false) ? 'ep-check-list-item' : 'ep-delete-circle');
	if(props?.all_translated ?? false) {
		status_icon_class.push('greenIcon');
	} else {
		status_icon_class.push('redIcon');
	}

	const has_label = (isNotEmpty(props.label_props, 'children')) ? true : false;

	return (
		<div>
			{(has_label) && (
				<>
					<FieldLabel
						{...props.label_props}
						_class={{ other:status_icon_class.join(" ") }}
					/>
				</>
			)}

			<div class="c">
				{props.children}

				{(!has_label) && (
					<span class={`is_field_status ${status_icon_class.join(" ")}`} />
				)}
			</div>
		</div>
	)
};

const DefaultTranslate = ({ field, loc, dispatch, ...props }) => {
	const in_process = props?.in_process ?? false;
	//const [ in_process, setInProcess ] = useState(props?.in_process ?? false);

	/*useEffect(() => {
		setInProcess(props.in_process);
	}, [ props.in_process ]);*/

	if(isEmpty(field, 'loc')) field.loc = {};
	const field_localized = (field.loc !== undefined && field.loc[loc] != undefined) ? field.loc[loc] : {};

	if(props.field_type == undefined) return null;

	let total_to_translate = (props?.field_type?.translate ?? []).length;
	let total_translated = 0;

	(props?.field_type?.translate ?? []).map((item) => {
		if(field_localized[item.key] == undefined || field_localized[item.key] == "") return;
		total_translated++;
	})

	const generic_wrapper_props = {
		any_translated:(total_translated > 0) ? true : false,
		all_translated:(total_to_translate == total_translated) ? true : false,
	};

	return (
		<div class="translatable_fields">
			{(isNotEmpty(props, 'section_name')) && (
				<p class="section_title">{props.section_name}</p>
			)}

			<div class="fields">
				{(props?.field_type?.translate ?? []).map((translate, t) => {
					if(!isNotEmpty(field, translate.key)) return null;

					if(translate.type == 'input') {
						return (
							<TranslateFieldWrapper
								key={`field_${t}`}
								{...generic_wrapper_props}
								label_props={{
									children:translate?.label ?? "Text",
								}}
							>
								<Input
									name={`fake_${translate.key}`}
									value={field[translate.key]}
									disabled={true}
									_class={{ first:true, other:'original' }}
								/>

								<span class="separator knk-arrow-r-big" />

								{(in_process === false || isNotEmpty(field_localized, translate.key)) ? (
									<Input
										name={translate.key}
										model={field_localized}
										change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v })}
										_class={{ first:true, other:'translation' }}
									/>
								) : (
									<div class="loading_field_emulator input_alike">
										<Spinner>Translating with AI...</Spinner>
									</div>
								)}
							</TranslateFieldWrapper>
						)
					}

					if(translate.type == 'image') {
						let full_image = (isNotEmpty(field_localized, 'name')) ? `${field.full_path}big1x/${field_localized.name}` : null;
						if (isEmpty(field_localized,'full_path') && !isEmpty(field_localized,`full_${translate.key}`)) {
							full_image = field_localized[`full_${translate.key}`];
						}

						const image_size = {
							width:(field.width != undefined) ? parseInt(field.width) : null,
							height:(field.height != undefined) ? parseInt(field.height) : null,
						};

						const original_image = isNotEmpty(field,'full_path') ? `${field.full_path}big1x/${field.name}`: field[`full_${translate.key}`];

						return (
							<TranslateFieldWrapper
								key={`field_${t}`}
								{...generic_wrapper_props}
								label_props={{
									children:translate?.label ?? "Image",
									addition:`${image_size.width}×${image_size.height}px`,
								}}
							>
								<img src={original_image} width="100%" />

								<span class="separator knk-arrow-r-big" />

								<AutoPostImageUploader
									{...translate.uploader}
									_size={image_size}
									// small_input={false}
									reduce_placeholder={(field.width != undefined || field.height != undefined) ? false : null}
									image={full_image}
									name={translate.key}
									model={field_localized}
									display_custom_dragging_instructions={props.intl.formatMessage({ id:"Upload image in {lang}" }, { lang:(window.available_langs != undefined) ? window.available_langs.find((obj) => (obj.key == loc)).lang : loc })}
									change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v })}
									_class={{ first:true, other:'translation' }}
								/>
							</TranslateFieldWrapper>
						)
					}

					if(translate.type == 'youtube') {
						return (
							<TranslateFieldWrapper
								key={`field_${t}`}
								{...generic_wrapper_props}
								label_props={{
									children:translate?.label ?? "YouTube video URL",
								}}
							>
								<Input
									name={`fake_${translate.key}`}
									value={field[translate.key]}
									disabled={true}
									_class={{ first:true, other:'original' }}
								/>

								<span class="separator knk-arrow-r-big" />

								<YouTubeField
									name={translate.key}
									model={field_localized}
									change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v, entity:props.entity, field_type:translate.type })}
									_class={{ first:true, other:'translation' }}
								/>
							</TranslateFieldWrapper>
						)
					}

					if(translate.type == 'url') {
						return (
							<TranslateFieldWrapper
								key={`field_${t}`}
								{...generic_wrapper_props}
								label_props={{
									children:translate?.label ?? "URL",
								}}
							>
								<Input
									name={`fake_${translate.key}`}
									value={field[translate.key]}
									disabled={true}
									_class={{ first:true, other:'original' }}
								/>

								<span class="separator knk-arrow-r-big" />

								<UrlField
									name={translate.key}
									model={field_localized}
									force_https={true}
									change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v })}
									_class={{ first:true, other:'translation' }}
									ensure_domain={translate.ensure_domain}
								/>
							</TranslateFieldWrapper>
						)
					}

					if(translate.type == 'pdf') {
						const full_image = (isNotEmpty(field_localized, 'name')) ? `${field.full_path}big1x/${field_localized.name}` : null;

						return (
							<TranslateFieldWrapper
								key={`field_${t}`}
								{...generic_wrapper_props}
								label_props={{
									children:translate?.label ?? "URL",
								}}
							>
								<Input
									name={`fake_${translate.key}`}
									value={field[translate.key]}
									disabled={true}
									_class={{ first:true, other:'original' }}
								/>

								<span class="separator knk-arrow-r-big" />

								<AutoPostImageUploader
									{...translate.uploader}
									allowed={{'application/pdf':1}}
									reduce_placeholder={(field.width != undefined || field.height != undefined) ? false : null}
									image={full_image}
									name={translate.key}
									model={field_localized}
									display_custom_dragging_instructions={props.intl.formatMessage({ id:"Upload image in {lang}" }, { lang:(window.available_langs != undefined) ? window.available_langs.find((obj) => (obj.key == loc)).lang : loc })}
									change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v })}
									_class={{ first:true, other:'translation' }}
								/>
							</TranslateFieldWrapper>
						)
					}

					if(translate.type == 'textarea') {
						return (
							<TranslateFieldWrapper
								key={`field_${t}`}
								{...generic_wrapper_props}
								label_props={{
									children:translate?.label ?? "Text",
								}}
							>
								<TextArea
									name={`fake_${translate.key}`}
									value={field[translate.key]}
									disabled={true}
									_class={{ first:true, other:'original' }}
								/>

								<span class="separator knk-arrow-r-big" />

								{(in_process === false || isNotEmpty(field_localized, translate.key)) ? (
									<TextArea
										name={translate.key}
										model={field_localized}
										change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v })}
										_class={{ first:true, other:'translation' }}
									/>
								) : (
									<div class="loading_field_emulator textarea_alike">
										<Spinner>Translating with AI...</Spinner>
									</div>
								)}
							</TranslateFieldWrapper>
						)
					}

					if(translate.type == 'richeditor') {
						return (
							<TranslateFieldWrapper
								key={`field_${t}`}
								{...generic_wrapper_props}
								label_props={{
									children:translate?.label ?? "Text",
								}}
							>
								<TextArea
									name={`fake_${translate.key}`}
									value={field[translate.key]}
									disabled={true}
									_class={{ first:true, other:'original' }}
								/>

								<span class="separator knk-arrow-r-big" />

								{(in_process === false || isNotEmpty(field_localized, translate.key)) ? (
									<RichEditor
										name={translate.key}
										model={field_localized}
										change={(k, v) => dispatch({ type:'TRANSLATE', id:field.id, loc, k, v })}
										_class={{ first:true, other:'translation' }}
									/>
								) : (
									<div class="loading_field_emulator textarea_alike">
										<Spinner>Translating with AI...</Spinner>
									</div>
								)}
							</TranslateFieldWrapper>
						)
					}
				})}
			</div>
		</div>
	)
};

const extractTranslationFields = (fields,field_types,locale) => {
	const indexed_types = {};
	const translate_fields = [];
	fields.map((field) => {
		if (indexed_types[field.type] == undefined) {
			indexed_types[field.type] = field_types.find((f) => (f.type == field.type));
		}
		if (indexed_types[field.type] == undefined) return;

		indexed_types[field.type].translate.map((translate_field) => {
			if (translate_field.type == 'image') return;
			if (translate_field.type == 'youtube') return;
			if (translate_field.type == 'media_url') return;

			if (field[translate_field.key] == undefined) return;
			if (field?.loc != undefined && field?.loc[locale] != undefined && (field?.loc[locale][translate_field.key] != undefined && field?.loc[locale][translate_field.key] != "")) {
				return;
			}

			translate_fields.push({
				key:`${field.id}.${translate_field.key}`,
				label:translate_field.key,
				value:field[translate_field.key],
			});
		})
	})

	return translate_fields
}


export const TranslationField = ({ fields, dispatch, ...props }) => {
	let langs = props.locales;
	
	const [ actual_lang, setActualLang ] = useState(langs[0]);
	const [ in_process, setInProcess ] = useState(false);

	const sections = [];
	langs.map((lang) => {
		sections.push({
			label:(window.available_langs != undefined) ? window.available_langs.find((obj) => (obj.key == lang)).lang : lang,
			onClick:() => setActualLang(lang),
			autotranslate:false,
			selected:(actual_lang == lang) ? true : false,
		});
	});

	const onAutotranslationChange = (s) => {
		setInProcess(s);
	};

	const entities = props?.entities ?? [];

	let total_items = fields.length;
	entities.map((ent) => {
		total_items += ent.fields.length;
	});	

	return (
		<div class="tabs_in_layer">
			<SectionTitle>Available translations</SectionTitle>

			<SectionSubtitle autotranslate={false}><FormattedMessage id="{num} elements to translate" values={{num:total_items}} /></SectionSubtitle>

			<NavigationTabs
				active={actual_lang}
				sections={sections}
				display_type='tab'
				_class={{ other:'gui_not_in_top_big gui_not_in_bottom sticky_in_layer' }}
				children_class="gui_is_not_padded"
			/>

			{(window.features?.includes('ai_translation')) && (
				<ButtonCollection
					_class={{ other:'not_in_bottom' }}
				>
					<AIMultiTranslate
						key={actual_lang}
						originals={extractTranslationFields(fields, props.field_types, actual_lang)}
						promotion_id={0}
						locale={actual_lang}
						disable_preview={true}
						onProgressChange={onAutotranslationChange}
						change={(k,v) => {
							const parts = k.split('.');
							k = parts.slice(1).join('.');
							dispatch({ type:'TRANSLATE', id:parts[0], loc:actual_lang, k, v });
						}}
					/>
				</ButtonCollection>
			)}

			<div class="all_translations">
				<TranslateFieldList
					{...props}
					fields={fields}
					dispatch={dispatch}
					in_process={in_process}
					section_name={props.main_section_name}
					section_name_with_index={props?.main_section_with_index ?? true}
					actual_lang={actual_lang}
				/>

				{entities.map((entity) => {
					return (
						<TranslateFieldList
							key={entity.name}
							{...props}
							{...entity}
							in_process={in_process}
							section_name={entity?.section_name ?? entity.name}
							section_name_with_index={entity?.section_with_index ?? (entity?.name_with_index ?? true)}
							actual_lang={actual_lang}
						/>
					);
				})}
			</div>
		</div>
	)
};

 const TranslateFieldList = ({ fields, actual_lang, dispatch, ...props }) => {
	const [ selected_field, setSelectedField ] = useState(false);

	const section_name_with_index = props?.section_name_with_index ?? true;

	return (
		<ul class="gui_translator">
			{fields.map((field, f) => {
				const li_class = ['single_field', 'gui_pos_rel'];
				if(f === selected_field) li_class.push('is_selected');

				const field_type = props.field_types.find((ft) => (ft.type == field.type));

				let section_name = (isNotEmpty(props, 'section_name')) ? `${props.section_name.toUpperCase()}` : null;
				if(section_name_with_index && section_name != undefined) section_name += ` #${f + 1}`;

				return (
					<li key={`field_${f}`} class={li_class.join(" ")} onClick={() => setSelectedField(f)}>
						{(props.translate_field == undefined) && (
							<DefaultTranslate
								key={`${field.id}_${actual_lang}`}
								section_name={section_name}
								field={field}
								loc={actual_lang}
								in_process={props.in_process}
								dispatch={dispatch}
								field_type={field_type}
								intl={props.intl}
							/>
						)}

						{(props.translate_field != undefined) && (
							React.cloneElement(props.translate_field, { field, loc:actual_lang, dispatch })
						)}
					</li>
				)
			})}
		</ul>
	);
};

export const TranslationLayer = ({ open, form, dispatch, field_types, saveTranslations, ...props }) => {
	const toggle = () => {
		dispatch({ type:'TOGGLE_TRANSLATE', id:open })
	};

	if(!open) return null;

	return(
		<FloatingLayer backButton={toggle}>
			<div>
				<TranslationField
					locales={props.locales}
					fields={(open == 'all') ? form : form.filter((f) => (f.id == open))}
					dispatch={dispatch}
					translate_field={props.translate_field}
					field_types={field_types}
					intl={props.intl}
				/>

				<ButtonCollection in_floating={true}>
					<SaveButton
						onClick={saveTranslations}
					>Save translations</SaveButton>
				</ButtonCollection>
			</div>
		</FloatingLayer>
	)
};

const SortableFields = SortableContainer(({ items, data, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const empty_message = props?.empty_message ?? `${i18n("There are no items")}.`;

	let ul_classes = ['gui_sortable_list', 'gui_overflowed_auto', 'gui_flexbox', 'gui_flex_start', 'gui_flex_col', 'gui_flex_nowrap'];
	if(props.ul_classes != undefined) ul_classes.push(props.ul_classes);

	return (
		<ul class={ul_classes.join(" ")} style={props.ul_styles}>
			{items.length > 0 && items.map((item, index) => {
				const fieldType = data.fields.find((f) => { return f.type == item.type });
				if(!fieldType) return;

				return (
					<SortableItem
						key={item.id}
						items={items}
						index={index}
						field={item}
						{...data}
						dispatch={props.dispatch}
					/>
				)
			})}

			{(items.length < 1) && (
				<li class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center">
					<div class="item_header">
						<div class="item_title_container gui_not_in_start_small">{empty_message}</div>
					</div>
				</li>
			)}
		</ul>
	);
});

const SortableItem = SortableElement((props) => {
	const field = props.field;
	const isOpen = field.id == props.open || props.single;
	const showHeader = true && !props.single;

	const change = (k, v, loc) => {
		props.dispatch({ type:'CHANGE_VALUE', id:field.id, k, v, loc });
	};

	const changeMulti = (data) => {
		props.dispatch({ type:'CHANGE_MULTI', id:field.id, data });
	}

	const fieldType = props.fields.find((f) => (f.type == field.type));

	let title = (isNotEmpty(field, 'short_title')) ? field.short_title : field.title;
	if(fieldType?.main_field != undefined) {
		let titlefield = field[fieldType.main_field];
		if(titlefield != undefined && titlefield != "") title = titlefield;
	}

	if(fieldType?.title != undefined) {
		title = fieldType.title(field);
	}

	let title_styles = null;
	if(fieldType?.titleStyles != undefined) {
		title_styles = fieldType.titleStyles(field);
	}

	let subtitle = (isNotEmpty(field, 'subtitle')) ? field.subtitle : false;
	if(fieldType?.subtitle != undefined) {
		subtitle = fieldType.subtitle(field);
		if(subtitle == undefined) subtitle = false;
	}

	let tooltip = false;
	if(fieldType?.tooltip != undefined) {
		tooltip = fieldType.tooltip(field);
	}

	const thumb_image = (isNotEmpty(field, 'image')) ? field.full_image : false;

	let icon = 'without_icon';
	if(!thumb_image && isNotEmpty(fieldType, 'icon')) {
		icon = fieldType.icon;
	} else {
		icon += ' gui_padded_in_start';
	}

	const _errors = props?.errors ?? [];
	const _errors_field = _errors[field.id] != undefined ? _errors[field.id] : false;

	return (
		<li
			class="gui_sortable_list_item gui_flexbox gui_flex_start gui_flex_col gui_flex_nowrap gui_flex_align_center"
			style={field.style}
		>
			{(showHeader) && (
				<div class={`item_header${(isOpen) ? ' is_open' : ''}`} style={title_styles}>
					{(props.sortable != undefined && props.sortable) && (
						<DragHandle />
					)}

					{(props.inline) ? (
						<div class="item_title_container is_inline">
							{React.cloneElement(props.element, {
								model:field,
								change:change,
								changeMulti:changeMulti,
								dynamicDispatch:props.dispatch,
							})}
						</div>
					) : (
						<div class="item_title_container">
							<ActionLink
								onClick={() => props.dispatch({ type:'SETOPEN', id:field.id })}
								icon={icon}
								autotranslate={false}
								_class={{ other:'item_title cutTextOnOverflow' }}
							>
								{(thumb_image !== false) && (
									<img
										src={thumb_image}
										height={props?.thumb_h ?? 20}
										alt=""
										class="img_thumb"
										style={{
											maxWidth:'none',
											flexShrink:'0',
										}}
									/>
								)}

								<strong>{title}</strong>
							</ActionLink>

							{(subtitle !== false) && (
								<span class="subtitle knk-white-space">[ {subtitle} ]</span>
							)}
						</div>
					)}

					<ButtonCollection>
						{(_errors_field) && (
							<SquaredButton
								color='red'
								icon='knk-alert'
								tooltip={<Tooltip with_icon={false} autotranslate={false}>{_errors_field}</Tooltip>}
								aria-label="View errors"
							/>
						)}

						{(tooltip) && (
							<SquaredButton
								icon='knk-information'
								tooltip={<Tooltip with_icon={false} autotranslate={false} position="top">{tooltip}</Tooltip>}
								aria-label="View information"
							/>
						)}

						{(!props.inline) && (
							<ToggleButton
								squared={true}
								onClick={() => props.dispatch({ type:'SETOPEN', id:field.id })}
								active={(field.id == props.open)? true : false}
								icon='knk-hide'
								icon_off='knk-unhide'
								aria-label="Toggle visibility"
							/>
						)}

						{(props.custom_button != undefined) && (
							props.custom_button(field)
						)}

						{(props.has_visibility) && (
							<ToggleButton
								squared={true}
								onClick={() => props.dispatch({ type:'TOGGLE_VISIBILITY', id:field.id })}
								active={field.status == undefined || field.status}
								icon='knk-visible'
								icon_off='knk-invisible'
								aria-label="Toggle visibility"
							/>
						)}

						{(props.has_translation && (window?.langs ?? []).length > 0) && (
							<SquaredButton
								onClick={() => props.dispatch({ type:'TOGGLE_TRANSLATE', id:field.id })}
								icon='knk-translate'
								aria-label="Translate"
							/>
						)}

						{(props.can_copy && ensureBoolean(field?.can_copy ?? true)) && (
							<SquaredButton
								disabled = {checkFieldLimits(fieldType,props.items)}
								onClick={() => props.dispatch({ type:'COPY', field })}
								icon='knk-copy'
								aria-label="Copy"
							/>
						)}

						{(props.can_delete && ensureBoolean(field?.can_delete ?? true)) && (
							<DeleteButton
								squared={true}
								_position="left"
								disabled={ensureBoolean(field?.disable_delete ?? false) || checkMinFieldLimit(fieldType,props.items)}
								tooltip={isNotEmpty(field, 'delete_tooltip') ? <Tooltip
									with_icon={false}
									position='left'
								>{field.delete_tooltip}</Tooltip> : null}
								onClick={() => props.dispatch({ type:'DELETE', id:field.id })}
								aria-label="Delete"
							/>
						)}
					</ButtonCollection>
				</div>
			)}

			{(isOpen) && (
				<div class="gui_item_unfoldable_options">
					{React.cloneElement(props.element, { model:field, errors:props.errors, change:change, changeMulti:changeMulti,single:props?.single })}
				</div>
			)}
		</li>
	)
});

const MenuItem = ({ e, fields, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const actual_items = (e?.max) ? fields.filter((f) => (f.type == e.type)).length : 1;

	const label = `${i18n(e.label)}${(e?.max) ? ` (${actual_items}/${e.max})` : ''}`;
	const disabled = checkFieldLimits(e,fields);

	return (
		<li>
			<ActionLink
				disabled={disabled}
				_class={{icon:e.icon, other:((e.premium) ? 'is_addon_this' : '')}}
				onClick={e.action}
				autotranslate={false}
			>{label}</ActionLink>
		</li>
	)
};


export const createField = (title, field_type) => {
	return {
		id:uuidv4(),
		title,
		type:field_type,
	};
};

export const translateAction = (array, action) => {
	if(action.type != 'TRANSLATE') return array;

	return array.map((field) => {
		if(field.id != action.id) return field;
		if(field.loc == undefined) field.loc = {};
		if(field.loc[action.loc] == undefined) field.loc[action.loc] = {};
		if(action.v == '' && field.loc[action.loc][action.k] != undefined) {
			delete field.loc[action.loc][action.k];
		} else {
			field.loc[action.loc][action.k] = action.v;
		}
		return { ...field };
	});
};

const DynamicFields = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [state, dispatch] = useReducer((state, action) => {
		switch(action.type) {
			case 'ADDFIELDAFTER':
				let new_arr = [];
				let id_after = uuidv4();
				for(var i in state.form) {
					new_arr.push(state.form[i]);
					if(state.form[i].id == action.id) {
						new_arr.push({
							id:id_after,
							title:i18n("Field"),
							type:action.field_type,
						});
					}
				}

				return {
					...state,
					open:props.inline ? false : id_after,
					form:[...new_arr]
				}

			case 'ADDFIELD':
				let id = uuidv4();
				return {
					...state,
					open:(props.inline) ? false : id,
					form:[
						...state.form,
						createField(i18n("Field"),action.field_type),
					],
				};

			case 'ADDFIELDWITHVALUES':
				let id_value = uuidv4();
				return {
					...state,
					form:[
						...state.form,
						{
							id:id_value,
							title:i18n("Field"),
							type:action.field_type,
							...action.values,
						},
					],
				};

			case 'SETFORM':
				return { ...state, form:action.fields };
		
			case 'ADDMODAL':
				return {...state,add_modal: (state.add_modal == false ? true : false)}

			case 'COPY':
				let copy = {
					...action.field,
					id:uuidv4(),
				};
				return { ...state, form:[...state.form, copy], open:copy.id };

			case 'SORT':
				let new_order = arrayMove(state.form, action.oldIndex, action.newIndex);
				return { ...state, form:[ ...new_order ] };

			case 'TOGGLE_TRANSLATE':
				return { ...state, translate:((action.id != state.translate) ? action.id : false) };

			case 'TRANSLATE':
				return { ...state, form:state.form.map((field) => {
					if(field.id != action.id) return field;
					if(field.loc == undefined ) field.loc = {};
					if(field.loc[action.loc] == undefined) field.loc[action.loc] = {};
					if(action.v == '' && field.loc[action.loc][action.k] != undefined) {
						delete field.loc[action.loc][action.k];
					} else {
						field.loc[action.loc] = {...field.loc[action.loc], [action.k]: action.v};
					}
					return {...field};
				})}

			case 'TOGGLE_VISIBILITY':
				const visibility_field = 'status';

				return {
					...state,
					form:state.form.map((field) => {
						if(field.id != action.id) return field;

						return {
							...field,
							[visibility_field]:(field[visibility_field] == undefined) ? false : !field[visibility_field],
						}
					}),
				};

			case 'DELETE':
				let newstate = { ...state, form:state.form.filter((f) => f.id != action.id)};
				return newstate;

			case 'DELETE_ALL':
				return {...state,form:[]};

			case 'SETOPEN':
				if(props.onOpen != undefined) {
					props.onOpen(action.id);
				}

				return { ...state, open:(action.id != state.open) ? action.id : false };
			
			case 'CHANGE_MULTI':
				for(var i in state.form) {
					if(state.form[i].id != action.id) continue;

					if(action.loc == undefined) {
						state.form[i] = { ...state.form[i], ...action.data };
						for(var j in action.data) {
							if (state.form[i][j] == "") {
								delete state.form[i][j];
							}
						}
					}
					return {...state,form:[...state.form]};
				}
				return state;

			case 'CHANGE_VALUE':
				for(var i in state.form) {
					if(state.form[i].id != action.id) continue;

					if(action.loc == undefined) {
						state.form[i] = { ...state.form[i], [action.k]:action.v };
					} else {
						if(state.form[i].loc == undefined) state.form[i].loc = {};

						if(state.form[i].loc[action.loc] == undefined) {
							if(action.v == "") {
								state.form[i] = {...state.form[i]};
								return { ...state, form:[...state.form] };
							}
							state.form[i].loc[action.loc] = {};
						}

						state.form[i].loc[action.loc][action.k] = action.v;
						if(action.v == '') {
							delete state.form[i].loc[action.loc][action.k];

							if(Object.keys(state.form[i].loc[action.loc]).length == 0) {
								delete state.form[i].loc[action.loc];
							}
						}
						state.form[i] = {...state.form[i]};
					}
					return {...state,form:[...state.form]};
				}
				return state;

			default:
				return state;
		}
	}, {
		form:props.fields,
		open:props?.open_field ?? false,
		translate:false,
		add_modal:false,
	});

	useEffect(() => {
		if(props.onChange != undefined) props.onChange(state.form);
	}, [ state.form ]);

	useEffect(() => {
		if(JSON.stringify(state.form) === JSON.stringify(props.fields)) {
			return;
		}
		dispatch({ type:'SETFORM', fields:props.fields });
	}, [ props.fields ]);

	useEffect(() => {
		if(props.onClickOpen != undefined) props.onClickOpen(state.open);
	}, [ state.open ]);

	const addField = (field_type) => {
		if (props.validator != undefined) {
			const new_field_values = props.validator(field_type,state.form);
			if (new_field_values == false) return;
			dispatch({ type:'ADDFIELDWITHVALUES', field_type, values:new_field_values });
			return;
		}
		dispatch({ type:'ADDFIELD', field_type });
	};

	const toggleAddModal = () => {
		dispatch({ type:'ADDMODAL'});
	};

	const onSortEnd = ({ oldIndex, newIndex }) => {
		dispatch({ type:'SORT', oldIndex, newIndex });
	};
	
	const addFieldValues = (values, type = null) => {
		type = type == null ? props.field_types[0].type : type;
		dispatch({ type:'ADDFIELDWITHVALUES', field_type:type, values });
	};

	let fields = [];
	if(props.fields != undefined) {
		props.field_types.map((f) => {
			let action = () => addField(f.type);

			if(f.add != undefined) {
				action = () => addFieldValues(f.add(f), f.type);
			}
			// if (f.add_options != undefined) {
			// 	action = () => toggleAddModal(f)
			// }
			fields.push({ ...f, action });
		});
	}

	const available_fields = fields.filter((f) => {
		if(props?.one_of_kind && props.fields.filter((a) => (a.type == f.type)).length > 0) return false;
		return true;
	});

	const can_copy = ensureBoolean(props.can_copy ?? true);
	const can_delete = ensureBoolean(props.can_delete ?? true);
	const can_create = props.can_create ?? (props?.single ? false :true);
	const hide_header = props?.hide_header ?? (props?.single ? false :false);
	const add_button_props = props?.add_button_props ?? {};
	const inline = props.inline ?? false;
	const section_title = (props.section_title != undefined) ? ((typeof props.section_title == 'string') ? i18n(props.section_title) : props.section_title) : i18n("Form fields");
	const subtitle = (isNotEmpty(props, 'subtitle')) ? props.subtitle : false;
	const section_classes = props?.section_classes ?? null;
	const section_icon = props?.section_icon ?? 'knk-settings';

	let add_field_label = props?.add_field_label ?? false;
	if(isNotEmpty(fields[0], 'label') && add_field_label === false) {
		add_field_label = fields[0].label;
	} else if(add_field_label === false) {
		add_field_label = "Add element";
	}

	let show_add_button = (props.max_items == undefined || state.form.length < props.max_items) ? true : false;
	if(!can_create) show_add_button = false;

	const saveTranslations = (e, cb) => {
		props.onChange(state.form);

		if(cb != undefined) cb();
		dispatch({ type:'TOGGLE_TRANSLATE', id:state.translate });
	};

	let ul_classes = props?.ul_classes ?? '';
	if(subtitle !== false) ul_classes += ' gui_not_in_top_small';

	return (
		<article class={section_classes}>
			{(!hide_header) && (
				<BuilderSubSectionTitle
					title={section_title}
					subtitle={(subtitle !== false) ? subtitle : null}
					icon={section_icon}
					bordered={props?.bordered ?? ((subtitle !== false) ? true : false)}
					title_actions={<ButtonCollection>
						{(show_add_button && props.add_options != undefined) && (
							<SquaredButton
								icon='knk-layers'
								tooltip={<Tooltip with_icon={false} position='bottom'>Create in bulk</Tooltip>}
								onClick={() => dispatch({ type:'ADDMODAL'})}
								aria-label="Create in bulk"
							/>
						)}

						{(show_add_button && props.custom_bulk_button != undefined) && (
							props.custom_bulk_button
						)}

						{(props.has_translation && (window?.langs ?? []).length > 0) && (
							<SquaredButton
								icon='knk-translate'
								onClick={() => dispatch({ type:'TOGGLE_TRANSLATE', id:'all' })}
								aria-label="Translate"
							/>
						)}

						{(props.custom_add_button != undefined) && (
							props.custom_add_button
						)}

						{(fields.length == 1 && props.custom_add_button == undefined && show_add_button) && (
							<CreateButton
								icon={add_button_props?.icon ?? false}
								color={add_button_props.color}
								onClick={fields[0].action}
							>{add_field_label}</CreateButton>
						)}

						{(props.custom_add_button == undefined && fields.length > 1 && can_create && available_fields.length > 0 && show_add_button) && (
							<FloatingSelectorList
								button_label={add_field_label}
								_elements={fields}
							>
								<MenuItem
									fields={state.form}
									intl={props.intl}
								/>
							</FloatingSelectorList>
						)}

						{(props.delete_all != undefined) && (
							<DeleteButton
								squared={true}
								tooltip={<Tooltip
									with_icon={false}
									position='right'
								>Delete all</Tooltip>}
								onClick={() => dispatch({ type:'DELETE_ALL'})}
								aria-label="Delete all"
							/>
						)}
					</ButtonCollection>}
				/>
			)}

			{(props.header != undefined) && (
				<div class="gui_sortable_list_header">{props.header}</div>
			)}

			{(state?.add_modal != false) && (
				<AddMenu
					add={addFieldValues}
					max_items={props.max_items ? props.max_items-state.form.length : null}
					upload={props.add_upload}
					img_size={props.img_size}
					type={props.field_types[0]}
					i18n={props.i18n}
					close={toggleAddModal}
					nameAutoTransform={props.nameAutoTransform}
				/>
			)}

			<SortableFields
				items={state?.form ?? []}
				data={{
					can_copy,
					can_delete,
					single:props.single ?? false,
					errors:props.errors,
					inline,
					has_visibility:props?.has_visibility ?? false,
					has_translation:props?.has_translation ?? false,
					element:props.children,
					fields,
					open:state.open,
					custom_button:props.custom_button,
					sortable:props?.sortable ?? true,
					thumb_h:props?.thumb_h,
				}}
				ul_classes={(ul_classes != '') ? ul_classes : null}
				ul_styles={props.ul_styles}
				intl={props.intl}
				useDragHandle={true}
				onSortEnd={onSortEnd}
				dispatch={dispatch}
				empty_message={props.empty_message}
			/>

			<TranslationLayer
				open={state.translate}
				form={state.form}
				locales={props.locales}
				field_types={props.field_types}
				dispatch={dispatch}
				saveTranslations={saveTranslations}
				intl={props.intl}
			/>

			{(props.extra_message != undefined) && (
				<span class="label_additional_msg gui_after_message whole_contents">
					{props.extra_message}
				</span>
			)}
		</article>
	)
}

export default injectIntl(DynamicFields);